// liveness.js — keep the WebSocket honest across iOS suspend/resume.
//
// iOS freezes a PWA's timers and sockets when it goes to background.
// On wake the TCP socket is often a zombie: home-assistant-js-websocket
// still believes it's connected, so service calls are written into a
// dead pipe and silently vanish until the socket eventually errors.
// That window is exactly the "I tapped and nothing happened" complaint.
//
// This watcher races connection.ping() against a short timeout on every
// "we may have been asleep" signal (visibilitychange → visible, window
// focus, network online, pageshow from the bfcache). A ping that doesn't
// answer in time means the socket is dead → connection.reconnect(true)
// force-discards it, which runs the library's close path immediately:
// in-flight commands reject, "disconnected" fires, backoff reconnect +
// resubscribe take over, then "ready" fires on the new socket.
//
// The connection's health is mirrored into onStateChange so the UI can
// show a reconnecting pill while the gap is open.

const PING_TIMEOUT_MS = 4000;
const MIN_CHECK_INTERVAL_MS = 5000;

export function attachLiveness(connection, { onStateChange, onAuthFailure } = {}) {
  let checking = false;
  let lastCheck = 0;

  const handleDisconnected = () => onStateChange?.('reconnecting');
  const handleReady = () => onStateChange?.('connected');
  // Fires when a reconnect attempt fails with invalid auth — the token
  // was revoked while we were away. Bubble up so the app can clear
  // stored tokens and restart the OAuth flow.
  const handleReconnectError = () => onAuthFailure?.();

  connection.addEventListener('disconnected', handleDisconnected);
  connection.addEventListener('ready', handleReady);
  connection.addEventListener('reconnect-error', handleReconnectError);

  const checkLiveness = async () => {
    // No socket means the library already knows it's down and its
    // backoff loop is running — nothing for us to probe.
    if (checking || !connection.socket) return;
    if (Date.now() - lastCheck < MIN_CHECK_INTERVAL_MS) return;
    checking = true;
    lastCheck = Date.now();
    try {
      await Promise.race([
        connection.ping(),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('ping timeout')), PING_TIMEOUT_MS)
        ),
      ]);
      // Pong came back — the socket is genuinely alive.
    } catch {
      if (connection.socket) connection.reconnect(true);
    } finally {
      checking = false;
    }
  };

  const onVisibilityChange = () => {
    if (!document.hidden) checkLiveness();
  };
  // bfcache restore (iOS Safari) skips visibilitychange in some paths.
  const onPageShow = (e) => {
    if (e.persisted) checkLiveness();
  };

  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('focus', checkLiveness);
  window.addEventListener('online', checkLiveness);
  window.addEventListener('pageshow', onPageShow);

  return () => {
    connection.removeEventListener('disconnected', handleDisconnected);
    connection.removeEventListener('ready', handleReady);
    connection.removeEventListener('reconnect-error', handleReconnectError);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('focus', checkLiveness);
    window.removeEventListener('online', checkLiveness);
    window.removeEventListener('pageshow', onPageShow);
  };
}
